// The venue logo (issue #20, US-5): PNG or SVG, small enough to keep the customer page inside its 150 KB budget
// (US-11), and an SVG that cannot run a script or load anything. Pure checks on the file's bytes, run by the settings
// page before the upload and by the API before the file reaches R2; the declared media type is never trusted.
import { UI, fill } from './copy';
import type { Lang } from './ticket';

/**
 * The largest logo accepted. The customer page budget (docs/customer-page-budget.md) counts the logo at this size
 * on top of the heaviest page: about 88 KB today, so 40 KB keeps every page under 150 KB with room to spare.
 */
export const LOGO_MAX_BYTES = 40 * 1024;

/** The largest side of a PNG logo, in pixels: a small file can still decode into a huge bitmap on a phone or a TV. */
export const LOGO_MAX_PX = 2048;

export type LogoType = 'png' | 'svg';

export const LOGO_MEDIA_TYPES: Record<LogoType, string> = { png: 'image/png', svg: 'image/svg+xml' };

/** The file picker's filter; the bytes are still checked. */
export const LOGO_ACCEPT = 'image/png,image/svg+xml,.png,.svg';

/** The refusals of a logo file; each is also a copy key. */
export const LOGO_ERRORS = ['logo_empty', 'logo_too_large', 'logo_bad_type', 'logo_dimensions', 'logo_unsafe'] as const;
export type LogoError = (typeof LOGO_ERRORS)[number];

export function isLogoError(v: unknown): v is LogoError {
  return typeof v === 'string' && (LOGO_ERRORS as readonly string[]).includes(v);
}

export type LogoCheck = { type: LogoType; width: number | null; height: number | null; error?: undefined } | { type?: undefined; error: LogoError };

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

function isPng(b: Uint8Array): boolean {
  return b.length >= 8 && PNG_SIGNATURE.every((v, i) => b[i] === v);
}

function u32(b: Uint8Array, at: number): number {
  return ((b[at] << 24) | (b[at + 1] << 16) | (b[at + 2] << 8) | b[at + 3]) >>> 0;
}

/** Decodes numeric character references (`&#106;`, `&#x6A;`), so an encoded `javascript:` is seen for what it is. */
function decodeCharRefs(s: string): string {
  return s.replace(/&#(x[0-9a-f]+|\d+);?/gi, (_, n: string) => {
    const code = n[0] === 'x' || n[0] === 'X' ? parseInt(n.slice(1), 16) : parseInt(n, 10);
    return Number.isFinite(code) && code >= 0 && code <= 0x10ffff ? String.fromCodePoint(code) : '';
  });
}

/** A reference that stays inside the file: a fragment (`#grad`) or an embedded raster image. */
function isLocalRef(v: string): boolean {
  const s = v.trim().toLowerCase();
  return s === '' || s.startsWith('#') || /^data:image\/(png|jpeg|gif|webp);base64,/.test(s);
}

/**
 * Entity declarations, or a DOCTYPE with an internal subset (where they live). A plain DOCTYPE line, as older
 * Illustrator exports write it, is accepted: browsers never fetch the external DTD.
 */
const DECLARES_ENTITIES = /<!ENTITY|<!DOCTYPE[^>]*\[/i;

/**
 * True when an SVG text could run code or reach out: scripts, event handlers, `javascript:` URLs, foreign HTML,
 * embedded documents, entity declarations, CSS imports, references outside the file, animations that rewrite a link.
 * The API also serves logos with a sandboxing Content-Security-Policy; this check keeps such files out entirely.
 */
export function svgIsUnsafe(svg: string): boolean {
  const text = decodeCharRefs(svg);
  const compact = text.replace(/\s+/g, '').toLowerCase();
  if (DECLARES_ENTITIES.test(text)) return true;
  if (/<(script|foreignobject|iframe|embed|object|audio|video|canvas|handler|listener)\b/i.test(text)) return true;
  if (/[\s"'/]on[a-z]+\s*=/i.test(text)) return true;
  if (compact.includes('javascript:') || compact.includes('vbscript:') || compact.includes('data:text') || compact.includes('@import')) return true;
  if (/attributename\s*=\s*["']\s*(xlink:)?href/i.test(text)) return true;
  for (const m of text.matchAll(/\b(?:xlink:)?href\s*=\s*(["'])([\s\S]*?)\1/gi)) if (!isLocalRef(m[2])) return true;
  for (const m of text.matchAll(/url\(\s*(['"]?)([\s\S]*?)\1\s*\)/gi)) if (!isLocalRef(m[2])) return true;
  return false;
}

/** An SVG document: optional BOM, XML declaration, comments and a plain DOCTYPE, then the `<svg` root. */
const SVG_ROOT = /^\uFEFF?\s*(<\?xml[\s\S]*?\?>\s*)?((<!--[\s\S]*?-->|<!DOCTYPE[^>[]*>)\s*)*<svg[\s>/]/i;

/**
 * Checks a logo file: not empty, at most LOGO_MAX_BYTES, a real PNG (signature and header, sides up to LOGO_MAX_PX)
 * or an SVG document in UTF-8 that `svgIsUnsafe` accepts. Returns the type sniffed from the bytes and, for a PNG,
 * its size in pixels.
 */
export function checkLogo(bytes: Uint8Array): LogoCheck {
  if (bytes.length === 0) return { error: 'logo_empty' };
  if (bytes.length > LOGO_MAX_BYTES) return { error: 'logo_too_large' };
  if (isPng(bytes)) {
    // The first chunk of a PNG is IHDR: length (4 bytes), "IHDR", width, height.
    if (bytes.length < 24 || String.fromCharCode(bytes[12], bytes[13], bytes[14], bytes[15]) !== 'IHDR') return { error: 'logo_bad_type' };
    const width = u32(bytes, 16);
    const height = u32(bytes, 20);
    if (width === 0 || height === 0) return { error: 'logo_bad_type' };
    if (width > LOGO_MAX_PX || height > LOGO_MAX_PX) return { error: 'logo_dimensions' };
    return { type: 'png', width, height };
  }
  let text: string;
  try {
    text = new TextDecoder('utf-8', { fatal: true, ignoreBOM: false }).decode(bytes);
  } catch {
    return { error: 'logo_bad_type' };
  }
  if (DECLARES_ENTITIES.test(text)) return { error: 'logo_unsafe' };
  if (!SVG_ROOT.test(text)) return { error: 'logo_bad_type' };
  if (svgIsUnsafe(text)) return { error: 'logo_unsafe' };
  return { type: 'svg', width: null, height: null };
}

/** The limit in kilobytes, as the copy states it. */
export function logoMaxKb(): number {
  return Math.round(LOGO_MAX_BYTES / 1024);
}

/** The copy of a logo refusal in `lang`, with the limits filled in. */
export function logoErrorCopy(lang: Lang, error: LogoError): string {
  return fill(UI[lang][error], { max: String(logoMaxKb()), px: String(LOGO_MAX_PX) });
}
